<template>
  <div>
    <div ref="sentinelRef" class="h-0" />
    <!-- Progress Banner -->
    <div
      ref="bannerRef"
      v-if="!is_disabled && !hide_controls"
      class="rounded-xl flex items-center gap-4 text-strongest z-50 transition-all duration-200"
      :class="[
        variant === 'primary' ? 'bg-container-bg-dis-canvas' : 'bg-container-bg',
        isStuck
          ? 'fixed top-[55px] lg:top-[72px] left-0 right-0 mx-auto max-w-[calc(theme(screens.maxcontent)+24px)] py-2 px-4 shadow-full border-b-2 border-filter-outline'
          : 'mb-2 lg:mb-6 p-4 ',
      ]"
    >
      <div class="relative w-9 h-9 flex items-center">
        <!-- Background circle -->
        <template v-if="answeredCount === schema_array.length">
          <div class="h-9 w-9 bg-success rounded-full flex items-center justify-center">
            <Icon name="Check" class="h-6 w-6 text-grey-1" />
          </div>
        </template>
        <svg v-else class="w-9 h-9 -rotate-90" viewBox="0 0 24 24">
          <circle
            cx="12"
            cy="12"
            r="10"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            class="text-outline-dividers"
          />
          <!-- Progress circle -->
          <circle
            cx="12"
            cy="12"
            r="10"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            class="text-success transition-all duration-300"
            :stroke-dasharray="circumference"
            :stroke-dashoffset="
              circumference - (answeredCount / schema_array.length) * circumference
            "
            stroke-linecap="round"
          />
        </svg>
      </div>
      <span class="font-inter font-semibold text-lg">
        <template v-if="group_label"
          >{{ group_label }} — {{ answeredCount }} of {{ schema_array.length }} answered</template
        >
        <template v-else>{{ answeredCount }}/{{ schema_array.length }} questions answered</template>
      </span>
    </div>
    <div
      v-if="isStuck && !is_disabled && !hide_controls"
      :style="{ height: bannerHeight + 'px' }"
      class="mb-2 lg:mb-6"
    />

    <!-- Question Cards -->
    <div class="flex flex-col gap-4 text-strongest">
      <div
        v-for="(field, index) in schema_array"
        :key="field.field_name"
        :ref="
          (el) => {
            if (el) questionRefs[field.field_name] = el;
          }
        "
        class="rounded-xl p-5"
        :class="variant === 'primary' ? 'bg-container-bg-dis-canvas' : 'bg-container-bg '"
      >
        <div
          :class="
            index > 0 || is_disabled ? 'flex flex-col md:flex-row md:items-start md:gap-6' : ''
          "
        >
          <!-- Question Title -->
          <div
            class="flex items-start gap-3"
            :class="index > 0 || is_disabled ? 'md:w-1/2 md:shrink-0' : ''"
          >
            <h3 class="font-semibold font-inter mb-4" :class="index > 0 ? 'md:mb-0' : ''">
              {{ index + 1 }}. {{ displayLabel(field) }}
            </h3>
          </div>

          <!-- Read-only display -->
          <div
            v-if="is_disabled"
            class="flex items-center gap-3"
            :class="index > 0 || is_disabled ? 'md:flex-1 md:justify-between' : ''"
          >
            <div class="py-2 px-3 rounded-xl bg-radio-checkbox-selector inline-block">
              <p class="text-sm font-medium font-inter md:text-base">
                {{ dataStore.contact.suitability_info[field.field_name] || "Not answered" }}
              </p>
            </div>
            <Icon name="Lock" class="h-[18px] w-[18px] stroke-strong shrink-0" />
          </div>

          <!-- Radio Options Grid (editable mode) -->
          <div
            v-else
            class="grid gap-2 text-strongest"
            :class="
              field.field_name === 'risk_willingness'
                ? 'grid-cols-1 md:flex-1'
                : index > 0 ? 'grid-cols-1 md:grid-cols-2 md:flex-1' : 'grid-cols-1 md:grid-cols-2'
            "
          >
            <label
              v-for="option in field.dropdown_options"
              :key="option"
              class="relative cursor-pointer"
            >
              <input
                type="radio"
                :name="field.field_name"
                :value="option"
                v-model="dataStore.contact.suitability_info[field.field_name]"
                class="sr-only"
              />
              <div
                class="flex items-center gap-3 py-3 px-2 rounded-xl"
                :class="
                  dataStore.contact.suitability_info[field.field_name] === option
                    ? 'bg-radio-checkbox-selector'
                    : ''
                "
              >
                <div
                  class="min-w-4 min-h-4 h-4 w-4 rounded-full border border-radio-checkbox-outline peer-checked:border-2 flex items-center justify-center"
                  :class="
                    dataStore.contact.suitability_info[field.field_name] === option
                      ? '!border-4 !border-radio-checkbox-checked'
                      : ''
                  "
                ></div>
                <div>
                  <p class="text-sm font-medium font-inter md:text-base">
                    {{
                      option.split("-").length > 1 && index === 0 ? option.split("-")[0] : option
                    }}
                  </p>
                  <span class="text-sm font-medium font-inter text-strong">
                    {{ option.split("-").length > 1 && index === 0 ? option.split("-")[1] : "" }}
                  </span>
                </div>
              </div>
            </label>

            <!-- Error shown once under all options -->
            <div
              v-if="getFieldErrorByName(field.field_name)"
              class="mt-2 ml-3 font-inter text-error text-sm flex col-span-full"
            >
              <Icon
                name="Asterisk"
                class="mr-1 h-[14px] min-h-[14px] w-[14px] min-w-[14px] mt-1 -skew-x-12 stroke-error"
                :stroke-width="3"
              />
              {{ getFieldErrorByName(field.field_name) }}
            </div>
          </div>
        </div>

        <!-- Conditional fields when employment_status is "Employed" or "Self-Employed" -->
        <div
          v-if="
            field.field_name === 'employment_status' &&
            !is_disabled &&
            (dataStore.contact.suitability_info?.employment_status === 'Employed' ||
              dataStore.contact.suitability_info?.employment_status === 'Self-Employed')
          "
          class="mt-4 flex flex-col md:flex-row gap-3"
        >
          <div class="flex-1">
            <BaseInput
              label="Occupation"
              field_name="occupation"
              field_type="string"
              v-model="dataStore.contact.occupation"
              :error_message="showEmploymentErrors && !dataStore.contact.occupation?.trim() ? 'This field is required.' : ''"
            />
          </div>
          <div class="flex-1">
            <BaseInput
              label="Employer"
              field_name="employer"
              field_type="string"
              v-model="dataStore.contact.employer"
              :error_message="showEmploymentErrors && !dataStore.contact.employer?.trim() ? 'This field is required.' : ''"
            />
          </div>
        </div>

        <!-- Conditional fields when employed_by_security_or_financial_regulatory is answered "Yes" -->
        <div
          v-if="
            field.field_name === 'employed_by_security_or_financial_regulatory' &&
            !is_disabled &&
            dataStore.contact.suitability_info?.employed_by_security_or_financial_regulatory ===
              'Yes'
          "
          class="mt-4 flex flex-col gap-3"
        >
          <div class="flex flex-col md:flex-row gap-3">
            <div class="flex-1">
              <BaseInput
                label="Employer Entity Name"
                field_name="securities_employer_name"
                field_type="string"
                v-model="dataStore.contact.securities_employer_name"
                :error_message="showEmployerErrors && !dataStore.contact.securities_employer_name?.trim() ? 'This field is required.' : ''"
              />
            </div>
          </div>
          <div class="flex flex-col gap-3">
            <BaseInput
              label="Street Address"
              field_name="securities_employer_street1"
              field_type="string"
              v-model="securitiesAddress.street1"
              :error_message="showEmployerErrors && !securitiesAddress.street1?.trim() ? 'This field is required.' : ''"
            />
            <BaseInput
              label="Apt, Suite, Unit (optional)"
              field_name="securities_employer_street2"
              field_type="string"
              v-model="securitiesAddress.street2"
            />
            <div class="flex flex-col md:flex-row gap-3">
              <div class="flex-1">
                <BaseInput
                  label="City"
                  field_name="city"
                  field_type="string"
                  v-model="securitiesAddress.city"
                  :error_message="showEmployerErrors && !securitiesAddress.city?.trim() ? 'This field is required.' : ''"
                />
              </div>
              <div class="flex-1">
                <BaseInput
                  label="State"
                  field_name="securities_employer_state"
                  field_type="dropdown"
                  :dropdown_options="US_STATES"
                  v-model="securitiesAddress.state"
                  :error_message="showEmployerErrors && !securitiesAddress.state?.trim() ? 'This field is required.' : ''"
                />
              </div>
              <div class="flex-1">
                <BaseInput
                  label="ZIP Code"
                  field_name="zip_code"
                  field_type="string"
                  v-model="securitiesAddress.zip_code"
                  :error_message="showEmployerErrors && !securitiesAddress.zip_code?.trim() ? 'This field is required.' : ''"
                />
              </div>
            </div>
          </div>
          <div>
            <div
              v-if="securitiesEmployerDoc"
              class="flex items-center gap-2 px-3 py-2 rounded-xl bg-container-bg border border-outline-dividers w-fit"
            >
              <Icon name="FileText" class="h-4 w-4 shrink-0 text-strong" />
              <span class="text-sm font-inter text-strongest truncate max-w-56">{{
                securitiesEmployerDoc.file_name
              }}</span>
              <button type="button" @click="removeSRFile" class="ml-1 flex items-center">
                <Icon name="X" class="h-4 w-4 text-strong hover:text-strongest" />
              </button>
            </div>
            <div
              v-else-if="isUploadingDoc"
              class="flex items-center gap-2 px-3 py-2 text-sm font-inter text-strong"
            >
              <span>Uploading...</span>
            </div>
            <label
              v-else
              class="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-outline-dividers bg-container-bg text-sm font-inter font-medium text-strongest hover:bg-radio-checkbox-selector transition-colors"
            >
              <Icon name="Paperclip" class="h-4 w-4" />
              <span>Attach Document</span>
              <input type="file" class="hidden" @change="handleSRFileChange" />
            </label>
          </div>
        </div>
      </div>
    </div>
    <div v-if="!hide_controls" class="justify-end mt-4 hidden lg:flex">
      <Button @click="$emit('continue')" size="lg" variant="primary-secondary">
        <template #label> Continue </template>
      </Button>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, reactive, ref, watch } from "vue";
import { Icon, Button, BaseInput } from "@/components";
import { US_STATES } from "@/constants";
import { useDataStore, usePurchaseFlowStore } from "@/stores";
import { serializeAddress, parseAddress } from "@/utilities";

const dataStore = useDataStore();
const purchaseFlowStore = usePurchaseFlowStore();

const sentinelRef = ref(null);
const bannerRef = ref(null);
const isStuck = ref(false);
const bannerHeight = ref(0);
const questionRefs = ref({});
let observer = null;
const circumference = 2 * Math.PI * 10;

const props = defineProps({
  schema_array: {
    type: Array,
    required: true,
  },
  variant: {
    type: String,
    default: "secondary",
  },
  is_disabled: {
    type: Boolean,
    default: false,
  },
  hide_controls: {
    type: Boolean,
    default: false,
  },
  show_errors: {
    type: Boolean,
    default: false,
  },
  // When set (paginated flow), the progress banner shows this group's name and
  // a "group-complete" event is emitted once every question in the group is answered.
  group_label: {
    type: String,
    default: "",
  },
});
const emit = defineEmits(["continue", "group-complete"]);

// Second-person phrasing overrides, keyed by field_name. Falls back to the backend
// label when a field isn't listed here.
const SECOND_PERSON_LABELS = {
  investment_objective: "What is your primary investment objective?",
  investment_experience: "How would you describe your investment experience?",
  investment_time_horizon: "What is your investment time horizon?",
  risk_willingness: "How much investment risk are you willing to take?",
  equities_allocation: "What portion of your portfolio is in equities?",
  bonds_allocation: "What portion of your portfolio is in bonds?",
  real_estate_allocation: "What portion of your portfolio is in real estate?",
  other_investments_allocation: "What portion of your portfolio is in other investments?",
  overall_portfolio_percentage:
    "What percentage of your overall portfolio will this investment represent?",
  significant_liquid_net_worth: "Do you have significant liquid net worth?",
  risky_investment_understanding:
    "Do you understand that this is a risky investment and you could lose your money?",
  employment_status: "What is your current employment status?",
  employed_by_security_or_financial_regulatory:
    "Are you employed by a securities or financial regulatory organization?",
};

function displayLabel(field) {
  return SECOND_PERSON_LABELS[field.field_name] ?? field.label;
}

onMounted(() => {
  if (bannerRef.value) {
    bannerHeight.value = bannerRef.value.offsetHeight;
  }

  observer = new IntersectionObserver(
    ([entry]) => {
      if (!entry.isIntersecting) {
        // Only stick when the sentinel has scrolled above the viewport, not when it's below (not yet reached)
        isStuck.value = entry.boundingClientRect.top < 0;
      } else {
        isStuck.value = false;
      }
    },
    { threshold: 0, rootMargin: "0px" }
  );

  if (sentinelRef.value) {
    observer.observe(sentinelRef.value);
  }
});

onUnmounted(() => {
  if (observer) {
    observer.disconnect();
  }
});

function scrollToFirstError() {
  // Check for validation errors first
  const firstErrorField = Object.keys(validationErrors.value)[0];
  if (firstErrorField && questionRefs.value[firstErrorField]) {
    questionRefs.value[firstErrorField].scrollIntoView({ behavior: "smooth", block: "center" });
    return true;
  }

  // Check for missing employment conditional fields
  const empStatus = dataStore.contact.suitability_info?.employment_status;
  if (
    (empStatus === "Employed" || empStatus === "Self-Employed") &&
    (!dataStore.contact.occupation?.trim() || !dataStore.contact.employer?.trim()) &&
    questionRefs.value["employment_status"]
  ) {
    questionRefs.value["employment_status"].scrollIntoView({ behavior: "smooth", block: "center" });
    return true;
  }

  // Check for unanswered questions
  const firstUnanswered = props.schema_array.find((field) => {
    const value = dataStore.contact.suitability_info[field.field_name];
    return value === null || value === undefined || value === "";
  });
  if (firstUnanswered && questionRefs.value[firstUnanswered.field_name]) {
    questionRefs.value[firstUnanswered.field_name].scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
    return true;
  }

  return false;
}

const isUploadingDoc = ref(false);
const showEmployerErrors = ref(false);
const showEmploymentErrors = ref(false);

const securitiesEmployerDoc = computed(
  () => dataStore.documents?.find((d) => d.file_category === "Securities Employer") ?? null
);

async function handleSRFileChange(event) {
  const file = event.target.files?.[0];
  if (!file) return;
  isUploadingDoc.value = true;
  try {
    await purchaseFlowStore.uploadSecuritiesEmployerDoc(file);
  } finally {
    isUploadingDoc.value = false;
  }
}

async function removeSRFile() {
  await purchaseFlowStore.deleteSecuritiesEmployerDoc(securitiesEmployerDoc.value);
}

// --- Structured employer address ---
// Rendered as discrete BaseInput fields (matching the sibling conditional fields) but
// serialized back into the single existing `securities_employer_address` string so no new
// backend fields are required.
const securitiesAddress = reactive({
  street1: "",
  street2: "",
  city: "",
  state: "",
  zip_code: "",
});

const securitiesAddressComplete = computed(
  () =>
    !!securitiesAddress.street1?.trim() &&
    !!securitiesAddress.city?.trim() &&
    !!securitiesAddress.state?.trim() &&
    !!securitiesAddress.zip_code?.trim()
);

let addressHydrated = false;
onMounted(() => {
  Object.assign(securitiesAddress, parseAddress(dataStore.contact.securities_employer_address));
  addressHydrated = true;
});

watch(
  securitiesAddress,
  () => {
    // Don't overwrite the stored value before the initial parse has run.
    if (!addressHydrated) return;
    dataStore.contact.securities_employer_address = serializeAddress(securitiesAddress);
  },
  { deep: true }
);

// Whether this rendered group actually contains the conditional-bearing questions.
// Guards prevent one group's isValid from being blocked by a conditional whose trigger
// question lives in a different paginated group.
const hasSecuritiesQuestion = computed(() =>
  props.schema_array.some(
    (f) => f.field_name === "employed_by_security_or_financial_regulatory"
  )
);
const hasEmploymentQuestion = computed(() =>
  props.schema_array.some((f) => f.field_name === "employment_status")
);

const answeredCount = computed(() => {
  return props.schema_array.filter((field) => {
    const value = dataStore.contact.suitability_info[field.field_name];
    return value !== null && value !== undefined && value !== "";
  }).length;
});

// Selection-based errors — only active when show_errors is true (e.g. modal edit after review failure).
// At the normal questionnaire step we only require all questions to be answered.
const validationErrors = computed(() => {
  if (!props.show_errors) return {};

  const errors = {};
  const suitabilityInfo = dataStore.contact.suitability_info || {};

  if (suitabilityInfo.significant_liquid_net_worth === "No") {
    errors.significant_liquid_net_worth = {
      message:
        'Your current selection is not appropriate for this investment. You must select "Yes" to continue.',
    };
  }

  if (suitabilityInfo.risky_investment_understanding === "No") {
    errors.risky_investment_understanding = {
      message:
        'Your current selection is not appropriate for this investment. You must select "Yes" to continue.',
    };
  }

  if (suitabilityInfo.self_responsibility === "No") {
    errors.self_responsibility = {
      message:
        'Your current selection is not appropriate for this investment. You must select "Yes" to continue.',
    };
  }

  const offeringTermYears = (purchaseFlowStore.selected_offering?.bond_term || 0) / 12;
  const timeHorizon = suitabilityInfo.investment_time_horizon;
  if (timeHorizon === "1-5 years" && offeringTermYears > 5) {
    errors.investment_time_horizon = {
      message: `Your investment time horizon (1–5 years) does not cover this offering's ${offeringTermYears}-year term. Please select a longer time horizon.`,
    };
  } else if (timeHorizon === "6-10 years" && offeringTermYears > 10) {
    errors.investment_time_horizon = {
      message: `Your investment time horizon (6–10 years) does not cover this offering's ${offeringTermYears}-year term. Please select a longer time horizon.`,
    };
  }

  return errors;
});

// Get error message for a field regardless of which option is selected
function getFieldErrorByName(fieldName) {
  const error = validationErrors.value[fieldName];
  return error ? error.message : null;
}

// Overall validation status
const isValid = computed(() => {
  const allAnswered = answeredCount.value === props.schema_array.length;
  const hasNoErrors = Object.keys(validationErrors.value).length === 0;

  if (
    hasSecuritiesQuestion.value &&
    dataStore.contact.suitability_info?.employed_by_security_or_financial_regulatory === "Yes" &&
    (!dataStore.contact.securities_employer_name?.trim() || !securitiesAddressComplete.value)
  ) {
    return false;
  }

  const empStatus = dataStore.contact.suitability_info?.employment_status;
  if (
    hasEmploymentQuestion.value &&
    (empStatus === "Employed" || empStatus === "Self-Employed") &&
    (!dataStore.contact.occupation?.trim() || !dataStore.contact.employer?.trim())
  ) {
    return false;
  }

  return allAnswered && hasNoErrors;
});

// Auto-advance: emit once this group is fully answered (paginated flow only, keyed by
// group_label). Fires on the transition into "complete", never on mount, so revisiting a
// finished group doesn't loop.
let wasGroupComplete = false;
watch(
  () => [answeredCount.value, isValid.value],
  () => {
    const complete = answeredCount.value === props.schema_array.length && isValid.value;
    if (complete && !wasGroupComplete && props.group_label && !props.is_disabled) {
      emit("group-complete");
    }
    wasGroupComplete = complete;
  }
);

// Expose validation method for parent to call directly
defineExpose({
  validate() {
    if (!isValid.value) {
      showEmployerErrors.value = true;
      showEmploymentErrors.value = true;
    }
    return {
      proceed: isValid.value,
      error_messages: validationErrors.value,
      answeredCount: answeredCount.value,
      totalQuestions: props.schema_array.length,
    };
  },
  scrollToFirstError,
});
</script>
